// Prize categories — "best junior", "U12", "best woman", "best unrated", "under 1600".
//
// WHY THIS IS NOT A SECOND RANKING. A category does not re-rank anybody. It takes the
// ranking the standings already produced, keeps the players who fall inside it, and
// numbers what is left 1, 2, 3. So the winner of the junior prize is whichever junior
// stands highest in the tournament, decided by the tournament's own tie-breaks — which
// is what a prize list means, and the only way the prize list and the standings can be
// made to agree.
//
// ONE CATEGORY IS A SET OF CONSTRAINTS, ALL OF WHICH MUST HOLD. That is deliberately
// duller than a rule language and covers the real cases in one shape: "best girl under
// 14" is an age bound and a sex together, not a new kind of rule. Every constraint is
// optional, and a category with none of them is the whole field.
//
// AGE IS AGE REACHED DURING THE TOURNAMENT'S YEAR, not age on the day. "U12" means a
// player who does not turn 13 in the year of the event, which is how FIDE and the
// Norwegian federation both define a junior class, and it is why the arithmetic here is
// `tournamentYear - birthYear` rather than anything involving the current date. A player
// whose birth year is unknown falls into no age-bounded category at all: guessing would
// quietly hand a prize to the wrong person, and leaving them out is visible.
//
// Pure: no DOM, no state, no Firestore. Rendering belongs to js/ui/categories.js.

import { playerBirthYear } from './formatting.js';

// A category's places default: three, which is what a prize table almost always has room
// for and what an arbiter would otherwise type once per category.
export const DEFAULT_PLACES = 3;

// The most this will list for one category. A "category" of 50 places is a standings
// table with extra steps, and the printed sheet has to end somewhere.
export const MAX_PLACES = 50;

function toNumberOrNull(value) {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function cleanText(value) {
    return String(value === null || value === undefined ? '' : value).trim();
}

/**
 * Read anything shaped roughly like a category into a full one.
 *
 * Same contract as the settings normalisers in js/state.js: the editor, a stored
 * tournament and a hand-edited document must all be read identically, or a category
 * would select one field on screen and another when the prize list is printed.
 */
export function normalizeCategory(raw) {
    const c = raw || {};
    const places = toNumberOrNull(c.places);
    return {
        id: cleanText(c.id) || ('cat_' + Math.random().toString(36).slice(2, 9)),
        name: cleanText(c.name),
        places: places !== null && places > 0 ? Math.min(Math.round(places), MAX_PLACES) : DEFAULT_PLACES,
        ageMax: toNumberOrNull(c.ageMax),
        ageMin: toNumberOrNull(c.ageMin),
        // 'w' / 'm', or '' for everybody. Stored as the TRF spells it, which is also what
        // the registration form and the player database already carry.
        sex: ['w', 'm'].includes(cleanText(c.sex).toLowerCase()) ? cleanText(c.sex).toLowerCase() : '',
        ratingMax: toNumberOrNull(c.ratingMax),
        ratingMin: toNumberOrNull(c.ratingMin),
        unratedOnly: !!c.unratedOnly,
        club: cleanText(c.club),
        fed: cleanText(c.fed).toUpperCase(),
        playerClass: cleanText(c.playerClass || c.class)
    };
}

export function normalizeCategories(raw) {
    return (Array.isArray(raw) ? raw : []).map(normalizeCategory).filter(c => c.name);
}

/**
 * A player's sex as 'm', 'w' or '' when unknown — read the same way wherever the field
 * is grouped or filtered on (here, and the Statistics window in js/utils/player-stats.js).
 */
export function playerSex(player) {
    const s = String((player && (player.sex || player.gender)) || '').trim().toLowerCase().charAt(0);
    // 'k' for "kvinne" is what the Norwegian registration form stores.
    if (s === 'k' || s === 'f' || s === 'w') return 'w';
    if (s === 'm') return 'm';
    return '';
}

/** A player's rating as a number, with "unrated" being 0 rather than a missing value. */
function ratingOf(player) {
    const n = parseInt(player && player.rating, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Does this player belong in this category?
 *
 * @param {Object} player
 * @param {Object} category  already normalised
 * @param {Object} ctx       { year } — the tournament's year, for the age bounds
 */
export function matchesCategory(player, category, ctx) {
    if (!player) return false;
    const c = category;
    const year = parseInt((ctx || {}).year, 10);

    if (c.ageMax !== null || c.ageMin !== null) {
        const born = parseInt(playerBirthYear(player), 10);
        // No birth year, or no year to measure against: the player is not placed in an
        // age category rather than being placed in the wrong one.
        if (!Number.isFinite(born) || !Number.isFinite(year)) return false;
        const age = year - born;
        if (c.ageMax !== null && age > c.ageMax) return false;
        if (c.ageMin !== null && age < c.ageMin) return false;
    }

    if (c.sex && playerSex(player) !== c.sex) return false;

    const rating = ratingOf(player);
    if (c.unratedOnly && rating > 0) return false;
    // A rating bound is a statement about a rating, so an unrated player is outside it —
    // not silently at zero, which would sweep every unrated player into "under 1600".
    if (c.ratingMax !== null) {
        if (rating === 0) return false;
        if (rating > c.ratingMax) return false;
    }
    if (c.ratingMin !== null) {
        if (rating === 0) return false;
        if (rating < c.ratingMin) return false;
    }

    if (c.club && cleanText(player.club).toLowerCase() !== c.club.toLowerCase()) return false;
    if (c.fed && cleanText(player.fed).toUpperCase() !== c.fed) return false;
    if (c.playerClass && cleanText(player.class).toLowerCase() !== c.playerClass.toLowerCase()) return false;

    return true;
}

/**
 * The prize list: every category with its own top places, in the order the categories
 * were defined.
 *
 * @param {Object} opts
 * @param {Array}  opts.order       the ranked rows from TieBreak.compute — [{ id, rank, points }]
 * @param {Array}  opts.players     the field
 * @param {Array}  opts.categories  raw or normalised categories
 * @param {number} opts.year        the tournament's year, for the age bounds
 * @param {boolean} [opts.all]      list every member, not only the prize places
 * @returns {Array<{category, rows:[{id, rank, place, points}], total:number}>}
 *   `rank` is the place in the TOURNAMENT, `place` the place in the category.
 */
export function categoryStandings({ order, players, categories, year, all } = {}) {
    const playerById = {};
    (players || []).forEach(p => { playerById[p.id] = p; });
    const ctx = { year };

    return normalizeCategories(categories).map(category => {
        const members = (order || []).filter(entry => {
            const player = playerById[entry.id];
            return player && matchesCategory(player, category, ctx);
        });
        const rows = members.map((entry, i) => ({
            id: entry.id,
            rank: entry.rank,
            place: i + 1,
            points: entry.points
        }));
        return {
            category,
            total: rows.length,
            rows: all ? rows : rows.slice(0, category.places)
        };
    });
}

/**
 * The categories a Norwegian club open almost always wants, offered as a starting point
 * so that the common case is one click rather than six forms.
 *
 * Names are English source strings like every other user-facing text here, and the
 * Norwegian dictionary carries the translations (see js/i18n/no.js).
 */
export function suggestedCategories() {
    return [
        { name: 'Best woman', sex: 'w' },
        { name: 'Best junior (U20)', ageMax: 20 },
        { name: 'U16', ageMax: 16 },
        { name: 'U14', ageMax: 14 },
        { name: 'U12', ageMax: 12 },
        { name: 'U10', ageMax: 10 },
        { name: 'Senior (60+)', ageMin: 60 },
        { name: 'Best unrated', unratedOnly: true }
    ].map(normalizeCategory);
}

/**
 * A one-line description of what a category selects, for the editor and the printed
 * sheet's legend. Returns translation templates rather than finished sentences, so the
 * caller runs them through tr() — the same contract the pairing refusals use.
 *
 * @returns {Array<{key: string, params: Object}>} one fragment per constraint, to be
 *   joined with a comma by the caller; empty when the category is the whole field.
 */
export function describeCategory(category) {
    const c = normalizeCategory(category);
    const parts = [];

    if (c.ageMax !== null && c.ageMin !== null) {
        parts.push({ key: 'aged {min}–{max}', params: { min: c.ageMin, max: c.ageMax } });
    } else if (c.ageMax !== null) {
        parts.push({ key: 'under {age}', params: { age: c.ageMax + 1 } });
    } else if (c.ageMin !== null) {
        parts.push({ key: '{age} and over', params: { age: c.ageMin } });
    }

    if (c.sex === 'w') parts.push({ key: 'women', params: {} });
    if (c.sex === 'm') parts.push({ key: 'men', params: {} });

    if (c.unratedOnly) parts.push({ key: 'unrated', params: {} });
    else if (c.ratingMin !== null && c.ratingMax !== null) {
        parts.push({ key: 'rating {min}–{max}', params: { min: c.ratingMin, max: c.ratingMax } });
    } else if (c.ratingMax !== null) {
        parts.push({ key: 'rating under {max}', params: { max: c.ratingMax + 1 } });
    } else if (c.ratingMin !== null) {
        parts.push({ key: 'rating {min} and above', params: { min: c.ratingMin } });
    }

    if (c.club) parts.push({ key: 'club {club}', params: { club: c.club } });
    if (c.fed) parts.push({ key: 'federation {fed}', params: { fed: c.fed } });
    if (c.playerClass) parts.push({ key: 'class {class}', params: { class: c.playerClass } });

    return parts;
}
